import { Link } from "wouter";
import { Logo } from "@/components/Logo";
import { ThemeToggle } from "@/components/ThemeToggle";

export default function DataDeletion() {
  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-50 bg-background/95 backdrop-blur border-b border-border">
        <div className="container mx-auto px-4 py-3 flex items-center justify-between gap-4">
          <Link href="/">
            <Logo size="md" />
          </Link>
          <ThemeToggle />
        </div>
      </header>

      <main className="container mx-auto px-4 py-12 max-w-4xl">
        <div className="prose dark:prose-invert max-w-none">
          <h1>Data Deletion Policy</h1>
          <p className="text-muted-foreground italic">
            Last Updated: February 22, 2026
          </p>

          <section className="mt-8">
            <h2>1. Your Right to Delete Your Data</h2>
            <p>
              At DailyHisaab, we respect your right to control your data. You
              may request the deletion of your account and all associated data
              at any time. This page explains what data we hold, how to request
              deletion, and what happens when your data is deleted.
            </p>
          </section>

          <section>
            <h2>2. What Data We Store</h2>
            <p>
              DailyHisaab stores the following data associated with your
              account:
            </p>

            <h3>2.1 In Firebase Cloud Firestore (Our Systems)</h3>
            <ul>
              <li>User profile (email, display name, user ID)</li>
              <li>
                Business profile (business name, address, phone, email, website,
                logo, GST details)
              </li>
              <li>
                App settings and preferences (user mode, sync status, theme)
              </li>
              <li>Reward points balance and history</li>
              <li>Pro mode subscription details and expiry</li>
            </ul>

            <h3>2.2 In Your Google Drive (Your Control)</h3>
            <ul>
              <li>
                Google Sheets containing your products and transactions data
              </li>
              <li>
                These files are in your personal Google Drive and are fully
                under your control
              </li>
            </ul>

            <h3>2.3 On Your Device (Local Storage)</h3>
            <ul>
              <li>Cached product and transaction data in IndexedDB</li>
              <li>Access tokens and preferences in localStorage</li>
            </ul>
          </section>

          <section>
            <h2>3. How to Request Data Deletion</h2>
            <p>
              You can request deletion of your data through any of the following
              methods:
            </p>

            <h3>Option A: Email Request</h3>
            <p>
              Send an email to <strong>support@dailyhisaab.com</strong> with the
              subject line "Data Deletion Request" and include:
            </p>
            <ul>
              <li>
                The email address associated with your DailyHisaab account
              </li>
              <li>Confirmation that you want all your data deleted</li>
            </ul>

            <h3>Option B: In-App</h3>
            <p>
              Go to <strong>Settings</strong> within the App and use the "Delete
              Account" option. Then filled the account deletion request form.
            </p>

            <p>
              We will process your request within <strong>30 days</strong> and
              send you a confirmation email once completed.
            </p>
          </section>

          <section>
            <h2>4. What Gets Deleted</h2>
            <p>
              When we process your deletion request, the following data will be
              permanently removed from our systems:
            </p>
            <ul>
              <li>
                Your user profile and account information from Firebase
                Firestore
              </li>
              <li>Your business profile and settings</li>
              <li>Your reward points balance and history</li>
              <li>Your Pro mode subscription records</li>
              <li>
                Any other data associated with your user ID in our database
              </li>
            </ul>
          </section>

          <section>
            <h2>5. What Is NOT Deleted By Us</h2>
            <p>
              The following data is outside our direct control and must be
              managed by you:
            </p>
            <ul>
              <li>
                <strong>Google Sheets files:</strong> Your product and
                transaction data stored in Google Sheets remains in your Google
                Drive. You can delete these files directly from your Google
                Drive at any time.
              </li>
              <li>
                <strong>Local device data:</strong> Cached data on your device
                can be cleared by clearing the App's browser data or
                uninstalling the App.
              </li>
              <li>
                <strong>Google account permissions:</strong> You should revoke
                DailyHisaab's access to your Google account at{" "}
                <a
                  href="https://myaccount.google.com/permissions"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Google Account Permissions
                </a>
                .
              </li>
            </ul>
          </section>

          <section>
            <h2>6. Data Deletion Timeline</h2>
            <ul>
              <li>
                <strong>Request acknowledgment:</strong> Within 2 business days
              </li>
              <li>
                <strong>Data deletion completed:</strong> Within 30 days of
                request
              </li>
              <li>
                <strong>Confirmation email:</strong> Sent upon completion
              </li>
            </ul>
          </section>

          <section>
            <h2>7. Consequences of Data Deletion</h2>
            <p>Please be aware that once your data is deleted:</p>
            <ul>
              <li>Your account cannot be recovered</li>
              <li>
                Your reward points and Pro mode status will be permanently lost
              </li>
              <li>
                You will need to create a new account if you wish to use
                DailyHisaab again
              </li>
              <li>
                Your Google Sheets data will remain intact in your Google Drive
                unless you delete it separately
              </li>
            </ul>
          </section>

          <section>
            <h2>8. Contact Us</h2>
            <p>
              For any questions about data deletion or this policy, please
              contact us:
            </p>
            <ul>
              <li>
                <strong>Email:</strong> support@dailyhisaab.com
              </li>
              <li>
                <strong>Legal:</strong> contact@asldigitalsolutions.in
              </li>
              <li>
                <strong>Website:</strong>{" "}
                <a
                  href="https://dailyhisaab.com"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  dailyhisaab.com
                </a>
              </li>
            </ul>
          </section>
        </div>
      </main>

      <footer className="border-t border-border py-8 mb-safe">
        <div className="container mx-auto px-4">
          <div className="flex flex-col items-center gap-6 text-center">
            <Link href="/">
              <Logo size="sm" />
            </Link>

            <div className="flex flex-wrap justify-center gap-x-6 gap-y-2 text-sm text-muted-foreground">
              <Link
                href="/privacy"
                className="hover:text-primary transition-colors whitespace-nowrap"
              >
                Privacy Policy
              </Link>
              <Link
                href="/terms"
                className="hover:text-primary transition-colors whitespace-nowrap"
              >
                Terms of Service
              </Link>
              <Link
                href="/data-deletion"
                className="hover:text-primary transition-colors whitespace-nowrap"
              >
                Data Deletion Policy
              </Link>
            </div>

            <div className="space-y-1">
              <p className="text-sm text-muted-foreground">
                Made with ❤️ in Bengal by{" "}
                <a
                  href="https://asldigitalsolutions.in"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary font-semibold hover:underline"
                >
                  ASL Digital Solutions
                </a>
              </p>
              <p className="text-sm text-muted-foreground">
                ©2026 DailyHisaab.🇮🇳 All Rights Reserved
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
